import { useRef, useEffect, type KeyboardEvent, type ClipboardEvent } from 'react'
import { cn } from '@/lib/utils'

interface OTPInputProps {
  length?: number
  value: string
  onChange: (value: string) => void
  onComplete?: (value: string) => void
  disabled?: boolean
  error?: boolean
  autoFocus?: boolean
  align?: 'start' | 'center'
}

export function OTPInput({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled,
  error,
  autoFocus = true,
  align = 'center',
}: OTPInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([])

  const digits = value.padEnd(length, ' ').slice(0, length).split('')

  useEffect(() => {
    if (autoFocus && !disabled) {
      refs.current[0]?.focus()
    }
  }, [autoFocus, disabled])

  const update = (next: string) => {
    const cleaned = next.replace(/\D/g, '').slice(0, length)
    onChange(cleaned)
    if (cleaned.length === length) onComplete?.(cleaned)
  }

  const handleChange = (index: number, char: string) => {
    const arr = digits.map((d) => (d === ' ' ? '' : d))
    arr[index] = char.replace(/\D/g, '').slice(-1)
    const next = arr.join('').replace(/\s/g, '')
    update(next)
    if (char && index < length - 1) refs.current[index + 1]?.focus()
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!digits[index]?.trim() && index > 0) {
        refs.current[index - 1]?.focus()
      } else {
        const arr = digits.map((d) => (d === ' ' ? '' : d))
        arr[index] = ''
        onChange(arr.join(''))
      }
    }
    if (e.key === 'ArrowLeft' && index > 0) refs.current[index - 1]?.focus()
    if (e.key === 'ArrowRight' && index < length - 1) refs.current[index + 1]?.focus()
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text')
    update(pasted)
    const focusIndex = Math.min(length - 1, pasted.replace(/\D/g, '').length)
    refs.current[focusIndex]?.focus()
  }

  const cellClass = (i: number) =>
    cn(
      'h-12 w-9 rounded-lg border-2 bg-neutral-50 text-center font-display text-xl font-bold text-neutral-900 sm:h-14 sm:w-11 sm:rounded-xl sm:text-2xl',
      'transition-all duration-150',
      'focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary-500/15',
      'disabled:cursor-not-allowed disabled:opacity-50',
      digits[i]?.trim() && !error && 'border-primary-300 bg-primary-50/40',
      error && 'border-danger-500 bg-danger-50/30 focus:border-danger-500 focus:ring-danger-500/15',
      !error && !digits[i]?.trim() && 'border-neutral-200',
    )

  const half = Math.floor(length / 2)

  return (
    <div
      className={cn(
        'flex items-center gap-1.5 sm:gap-2.5',
        align === 'center' ? 'justify-center' : 'justify-start',
      )}
    >
      {Array.from({ length }).map((_, i) => (
        <div key={i} className="flex items-center gap-1.5 sm:gap-2.5">
          {i === half && length > 4 && (
            <span className="select-none px-0.5 text-xl font-light text-neutral-300" aria-hidden>
              –
            </span>
          )}
          <input
            ref={(el) => { refs.current[i] = el }}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            disabled={disabled}
            value={digits[i]?.trim() ? digits[i] : ''}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            aria-label={`Digit ${i + 1} of ${length}`}
            className={cellClass(i)}
          />
        </div>
      ))}
    </div>
  )
}
